import { httpService } from "./http.service";

// Metrics API base URL - uses dashboard service
const METRICS_BASE_URL = "/dashboard/generic/metrics";

// Types for metrics API responses
export interface Metric {
  metricId: string;
  metricName: string;
}

export interface MetricDataPoint {
  startDateTime: string;
  endDateTime: string;
  value: string;
}

export interface MetricCalculateResponse {
  metricName: string;
  values: MetricDataPoint[];
}

export interface Doctor {
  fullName: string;
  lastName: string;
  firstName: string;
}

export interface CptCodesResponse {
  codes: string[];
}

export interface PayerRolesResponse {
  codes: string[];
}

export interface MetricCalculateParams {
  metricId: string;
  startDate: string;
  endDate: string;
  period?: number;
  doctorName?: string;
  codes?: string;
  payerRole?: string;
}

class MetricsService {
  /**
   * Get list of all available metrics
   * GET /api/generic/metrics
   */
  async getMetrics(): Promise<Metric[]> {
    return httpService.get<Metric[]>(METRICS_BASE_URL);
  }

  /**
   * Get list of all doctors
   * GET /api/data/doctors
   */
  async getDoctors(): Promise<Doctor[]> {
    return httpService.get<Doctor[]>("/dashboard/data/doctors");
  }

  /**
   * Get list of all CPT codes
   * GET /api/data/cptcodes
   */
  async getCptCodes(): Promise<CptCodesResponse> {
    return httpService.get<CptCodesResponse>("/dashboard/data/cptcodes");
  }

  /**
   * Get list of all payer roles
   * GET /api/data/payer-roles
   */
  async getPayerRoles(): Promise<PayerRolesResponse> {
    return httpService.get<PayerRolesResponse>("/dashboard/data/payer-roles");
  }

  /**
   * Calculate metric data for a given date range
   * GET /api/generic/metrics/calculate?metricId=xxx&startDate=YYYY-MM-DD&endDate=YYYY-MM-DD&period=1
   */
  async calculateMetric(
    metricId: string,
    startDate: string,
    endDate: string,
    period: number = 1,
    options?: {
      doctorName?: string;
      codes?: string;
      payerRole?: string;
    }
  ): Promise<MetricCalculateResponse> {
    const params = new URLSearchParams({
      metricId,
      startDate,
      endDate,
      period: period.toString(),
    });

    // Build query string manually to avoid encoding commas
    let queryString = params.toString();

    if (options?.doctorName) {
      queryString += `&doctorName=${options.doctorName}`;
    }
    if (options?.codes) {
      queryString += `&codes=${options.codes}`;
    }
    if (options?.payerRole) {
      queryString += `&payerRole=${options.payerRole}`;
    }

    return httpService.get<MetricCalculateResponse>(`${METRICS_BASE_URL}/calculate?${queryString}`);
  }

  /**
   * Calculate multiple metrics at once
   */
  async calculateMultipleMetrics(
    metricIds: string[],
    startDate: string,
    endDate: string,
    period: number = 1,
    options?: {
      doctorName?: string;
      codes?: string;
      payerRole?: string;
    }
  ): Promise<MetricCalculateResponse[]> {
    const promises = metricIds.map((metricId) =>
      this.calculateMetric(metricId, startDate, endDate, period, options)
    );
    return Promise.all(promises);
  }
}

export const metricsService = new MetricsService();
